import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDailyGoalSettings, DailyGoalType } from '@/hooks/useDailyGoalSettings';
import { Target, Clock, BookOpen, List, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const goalTypeOptions: { value: DailyGoalType; label: string; icon: React.ReactNode; unit: string; description: string }[] = [
  {
    value: 'time',
    label: 'Time',
    icon: <Clock className="w-4 h-4" />,
    unit: 'minutes',
    description: 'Track reading time via Focus Mode',
  },
  {
    value: 'pages',
    label: 'Pages',
    icon: <BookOpen className="w-4 h-4" />,
    unit: 'pages',
    description: 'Track pages read when logging progress',
  },
  {
    value: 'chapters',
    label: 'Chapters',
    icon: <List className="w-4 h-4" />,
    unit: 'chapters',
    description: 'Track chapters completed',
  },
];

export function DailyGoalSettings() {
  const { settings, loading, saving, saveSettings, getDefaultTarget } = useDailyGoalSettings();
  const [type, setType] = useState<DailyGoalType>(settings.type);
  const [target, setTarget] = useState(settings.target);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setType(settings.type);
    setTarget(settings.target);
  }, [settings]);

  useEffect(() => {
    setHasChanges(type !== settings.type || target !== settings.target);
  }, [type, target, settings]);

  const handleTypeChange = (newType: DailyGoalType) => {
    setType(newType);
    // Set a sensible default for the new type
    setTarget(getDefaultTarget(newType));
  };

  const handleSave = async () => {
    const success = await saveSettings({ type, target });
    if (success) {
      toast.success('Daily goal updated! 🎯');
      setHasChanges(false);
    } else {
      toast.error('Failed to update daily goal');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Daily Reading Goal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentOption = goalTypeOptions.find(opt => opt.value === type)!;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" />
          Daily Reading Goal
        </CardTitle>
        <CardDescription>
          Choose how you want to track your daily reading progress
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Goal Type Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Track by</Label>
          <RadioGroup
            value={type}
            onValueChange={(value) => handleTypeChange(value as DailyGoalType)}
            className="grid grid-cols-3 gap-3"
          >
            {goalTypeOptions.map((option) => (
              <div key={option.value}>
                <RadioGroupItem
                  value={option.value}
                  id={`goal-type-${option.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`goal-type-${option.value}`}
                  className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer transition-all"
                >
                  <div className="mb-2 rounded-full bg-primary/10 p-2">
                    {option.icon}
                  </div>
                  <span className="text-sm font-medium">{option.label}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
          <p className="text-xs text-muted-foreground mt-2">
            {currentOption.description}
          </p>
        </div>

        {/* Target Value */}
        <div className="space-y-2">
          <Label htmlFor="daily-goal-target" className="text-sm font-medium">
            Daily target
          </Label>
          <div className="flex items-center gap-3">
            <Input
              id="daily-goal-target"
              type="number"
              min={1}
              max={type === 'time' ? 480 : type === 'pages' ? 500 : 20}
              value={target}
              onChange={(e) => setTarget(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-24"
            />
            <span className="text-sm text-muted-foreground">{currentOption.unit} per day</span>
          </div>
        </div>

        {/* Quick presets */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Quick presets</Label>
          <div className="flex flex-wrap gap-2">
            {type === 'time' && (
              <>
                <Button variant="outline" size="sm" onClick={() => setTarget(10)}>10 min</Button>
                <Button variant="outline" size="sm" onClick={() => setTarget(15)}>15 min</Button>
                <Button variant="outline" size="sm" onClick={() => setTarget(30)}>30 min</Button>
                <Button variant="outline" size="sm" onClick={() => setTarget(60)}>1 hour</Button>
              </>
            )}
            {type === 'pages' && (
              <>
                <Button variant="outline" size="sm" onClick={() => setTarget(10)}>10 pages</Button>
                <Button variant="outline" size="sm" onClick={() => setTarget(20)}>20 pages</Button>
                <Button variant="outline" size="sm" onClick={() => setTarget(50)}>50 pages</Button>
                <Button variant="outline" size="sm" onClick={() => setTarget(100)}>100 pages</Button>
              </>
            )}
            {type === 'chapters' && (
              <>
                <Button variant="outline" size="sm" onClick={() => setTarget(1)}>1 chapter</Button>
                <Button variant="outline" size="sm" onClick={() => setTarget(2)}>2 chapters</Button>
                <Button variant="outline" size="sm" onClick={() => setTarget(3)}>3 chapters</Button>
                <Button variant="outline" size="sm" onClick={() => setTarget(5)}>5 chapters</Button>
              </>
            )}
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Goal'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
